// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import cartLineSharedStyles from "@guestsupply/App/SharedStyles/CartLineSharedStyles";
import { FormFieldClickable } from "@guestsupply/MobiusOverrides/FormField/FormField";
import { requestRmaProductListStyles } from "@guestsupply/Overrides/Widgets/RequestRma/RequestRmaProductList";
import { color, mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import { css } from "styled-components";

requestRmaProductListStyles.productTotalPriceHeadingAndText = {
    ...cartLineSharedStyles.extendedUnitNetPriceText,
};

requestRmaProductListStyles.productDescriptionStyles = {
    ...cartLineSharedStyles.productDescription,
};

requestRmaProductListStyles.productPartNumbers = {
    ...cartLineSharedStyles.productPartNumbers,
};

requestRmaProductListStyles.returnReasonSelect = {
    mergeCss: true,
    cssOverrides: {
        formField: css`
            width: auto;
            margin-top: 20px;
        `,
        formInputWrapper: css`
            max-width: 230px;
            ${xScreen("lte", "xs")} {
                max-width: 100%;
                min-width: unset;
            }
        `,
        inputSelect: css`
            height: 33px;
        `,
    },
    iconProps: {
        css: css`
            &&& {
                display: flex;
                justify-content: flex-end;
                padding-right: 14px;
                width: auto;
                top: 1px;
            }
            svg {
                width: 9px;
                height: 13px;
            }
        `,
    },
};

requestRmaProductListStyles.actionsContainer = {
    css: css`
        display: flex;
        align-items: baseline;
        width: 100%;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
        }
    `,
};

requestRmaProductListStyles.gridContainer = {
    css: css`
        margin-top: 25px;
        margin-bottom: 25px;
    `,
};

requestRmaProductListStyles.priceHeading = {
    ...cartLineSharedStyles.extendedUnitNetPriceText?.extendedStyles?.heading,
};

requestRmaProductListStyles.priceText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText?.extendedStyles?.text, {
        css: css`
            display: inline-flex;
            width: auto;
            ${xScreen("lte", "md")} {
                padding-top: 0;
            }
            padding-top: 0;
        `,
    }),
};

requestRmaProductListStyles.unitOfMeasure = {
    ...mergeWithCss(cartLineSharedStyles.unitOfMeasure, {
        css: css`
            display: inline-flex;
            line-height: 17px;
            margin-left: 4px;
        `,
    }),
};

requestRmaProductListStyles.lineHeader = {
    wrapper: {
        css: css`
            grid-template-columns: 0.5fr 1.5fr 1fr 1fr 1fr 1fr;
        `,
    },
    total: {
        css: css`
            grid-column: 6/ 7;
            ${xScreen("lte", "md")} {
                &&& {
                    grid-row: 3 / 4;
                }
            }
        `,
    },
    surcharges: {
        css: css`
            grid-column: 5/ 6;
            padding: 8px 0;
            border-right: 1px solid ${color("common.border")};
            ${xScreen("lte", "md")} {
                &&& {
                    grid-row: 4 / 5;
                    grid-column: 3 / 5;
                }
            }
        `,
    },
};

requestRmaProductListStyles.cartLineContainer = {
    container: {
        css: css`
            grid-template-columns: 0.5fr 1.5fr 1fr 1fr 1fr 1fr;
        `,
    },
    total: {
        css: css`
            grid-column: 6 / 7;
            ${xScreen("lte", "md")} {
                grid-row: 4 / 5;
                grid-column: 5 / 7;
            }
        `,
    },
    surcharges: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-row: 4 / 5;
                grid-column: 3 / 5;
            }
        `,
    },
    actions: {
        css: css`
            grid-column: 2 / 4;
        `,
    },
};
requestRmaProductListStyles.returnReasonContainer = {
    css: css`
        display: flex;
        flex-direction: row;
        align-items: baseline;
        gap: 20px;
        div[class^="UploadWrapper"] {
            align-items: flex-start;
        }
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            align-items: flex-start;
        }
    `,
};

requestRmaProductListStyles.fileUploader = {
    mergeCss: true,

    buttonProps: {
        variant: "tertiary",
        css: css`
            margin: 22px 0 0 20px;
            line-height: 31px;
            ${xScreen("lte", "sm")} {
                margin: 23px 0 0 15px;
            }

            ${xScreen("lte", "xs")} {
                width: 100%;
                min-width: unset;
                margin: 20px 0;
                text-align: center;
            }
        `,
    },
    // XNG-Changes: GSS-368
    cssOverrides: {
        inputSelect: css`
            min-width: 230px;
            height: 33px;
        `,
        appearanceTransition: css`
            ${FormFieldClickable} {
                right: 24px;
            }
        `,
    },
};

requestRmaProductListStyles.addToListLink = {
    ...mergeWithCss(cartLineSharedStyles.addToListLink, {
        css: css`
            width: max-content;
        `,
    }),
};

requestRmaProductListStyles.tooltipStyles = {
    cssOverrides: {
        tooltipClickable: css`
            display: flex;
            margin-left: 5px;
            ${IconWrapper} {
                color: ${color("primary.main")};
                height: 13px;
                width: 13px;
            }
        `,
        tooltipWrapper: css`
            display: inline-block;
        `,
    },
};

requestRmaProductListStyles.priceWrapper = {
    css: css`
        display: flex;
        align-items: center;
    `,
};

requestRmaProductListStyles.qtyWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        padding-right: 10px;
    `,
};

requestRmaProductListStyles.qtyLabel = {
    size: 12,
    weight: 500,
    lineHeight: "15px",
    css: css`
        margin-bottom: 8px;
    `,
};

requestRmaProductListStyles.qtyToReturnField = {
    editableQuantityTextField: {
        cssOverrides: {
            inputSelect: css`
                max-width: unset;
                width: 70px;
            `,
        },
    },
    controlsWrapper: {
        css: css`
            position: absolute;
            left: 70px;
        `,
    },
};

requestRmaProductListStyles.fileUploaderNote = {
    size: 12,
    weight: 400,
    lineHeight: "15px",
};
